import React from 'react';
import numeral from 'numeral';
import { naira } from '../../../../../constants';
import { getOptionPrice } from '../../../../../helpers';
import {
  add,
  search,
  minusIcon,
  SvgCheck,
  SvgRadio,
} from '../../../../../assets/svg';
import { FoodGroup, Option, OrderItem } from '../../../../../types';
import { PrimaryButton, SecondaryButton } from '../../../../components';
import {
  FoodGroupSectionProps,
  FoodItemQuantityProps,
  FoodItemSelectorProps,
  SelectOptionsProps,
} from './types';
import { v4 as uuid } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import {
  categorySelector,
  getAllCategories,
} from '../../../../../store/slices';

export const FoodItemSelector = ({
  edit,
  foodItems,
  orderItemList,
  close,
  setOrderItemList,
  calculateCart,
}: FoodItemSelectorProps) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = React.useState(1);
  const [orderItem, setOrderItem] = React.useState<OrderItem>({
    foodItem: '',
    quantity: 1,
    options: [],
    customisations: [],
    price: 0,
  });
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [categoryFilter, setCategoryFilter] = React.useState('');

  const filteredFoodItems = foodItems.filter((item) => {
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesCategory =
      !categoryFilter ||
      item.categories?.some(
        (category) =>
          category.name.toLowerCase() === categoryFilter.toLowerCase()
      );

    return matchesSearch && matchesCategory;
  });

  const { categories } = useAppSelector(categorySelector);

  const addItem = () => {
    let list = [...orderItemList];

    if (edit) {
      list = list.filter((i) => i.token !== edit);
      list.push(orderItem);
    } else {
      list.push(orderItem);
    }

    setOrderItemList(list);
    const cartTotal = calculateCart();

    close();

    return cartTotal;
  };

  const filterFoodItems = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;

    setCategoryFilter(value);
  };

  React.useEffect(() => {
    if (edit) {
      const itemToEdit = orderItemList.find((i) => i.token === edit);

      itemToEdit && setOrderItem(itemToEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  React.useEffect(() => {
    categories && categories.length === 0 && dispatch(getAllCategories());
    // eslint-disable-next-line
  }, [dispatch]);

  return (
    <div className="p-6 px-8 bg-white rounded">
      <p className="text-left bold">Create an order item</p>
      {step === 1 && (
        <>
          <div className="flex justify-between">
            <p className="text-xs text-left mt-2">Select the food item</p>
            <select
              name="Category"
              value={categoryFilter}
              className="range-select bg-chevron-down h-2"
              onChange={filterFoodItems}
            >
              <option value="">Categories</option>
              {categories.map(({ id, name }) => (
                <option key={id} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-1 p-3 items-center rounded-lg w-full mt-6 mb-4 bg-grey-2">
            <img alt="search icon" src={search} />
            <input
              type="text"
              placeholder="Search food items"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="focus:outline-none w-full p-1 bg-grey-2"
            />
          </div>
          <div className="h-[400px] overflow-y-auto">
            <div className="grid grid-cols-3 gap-4 items-start">
              {filteredFoodItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setOrderItem({
                      ...orderItem,
                      foodItem: item.id,
                      name: item.name,
                      price: item.price,
                      brand: item.brand,
                      token: uuid(),
                    });
                    setStep(2);
                  }}
                  className={`relative flex items-center justify-between w-full px-4 py-3 rounded-lg border border-neutral-300 hover:bg-pink transition-colors ${
                    item.id === orderItem.foodItem
                      ? 'border-primary-600 bg-primary-200'
                      : 'bg-[#FCF7F8]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={item.imageUrl ?? ''}
                        alt="pic"
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <p className="text-sm truncate">{item.name}</p>
                      <p className="text-xs bold">{`${naira} ${numeral(
                        item.price
                      ).format('0,0')}`}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex justify-between">
            <SecondaryButton text="Cancel" onClick={close} />
          </div>
        </>
      )}

      {step === 2 && (
        <SelectOptions
          setStep={setStep}
          orderItem={orderItem}
          foodItems={foodItems}
          setOrderItem={setOrderItem}
        />
      )}

      {step === 3 && (
        <SelectCustomizations
          setStep={setStep}
          orderItem={orderItem}
          foodItems={foodItems}
          setOrderItem={setOrderItem}
          addItem={addItem}
        />
      )}
    </div>
  );
};

const SelectOptions = ({
  foodItems,
  orderItem,
  setStep,
  setOrderItem,
}: SelectOptionsProps) => {
  const item = foodItems.find((i) => i.id === orderItem.foodItem);
  const foodGroups = (item?.foodGroup as FoodGroup[]) || [];
  const groupCount = foodGroups.length;
  const rowCount = Math.ceil(groupCount / 2);

  return (
    <>
      <p className="text-xs text-left mt-2">
        Select the options to be added to this food item
      </p>

      {/* <div className="grid grid-cols-2 gap-4 mt-7 h-[400px] overflow-y-auto items-start">
        {item &&
          (item.foodGroup as FoodGroup[])?.map((group) => (
            <FoodGroupSection
              key={group.id}
              group={group}
              orderItem={orderItem}
              setOrderItem={setOrderItem}
            />
          ))}
      </div> */}

      <div
        className={`grid grid-cols-2 gap-4 mt-7 h-[400px] overflow-y-auto items-start`}
        style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
      >
        {foodGroups.map((group) => (
          <FoodGroupSection
            key={group.id}
            group={group}
            orderItem={orderItem}
            setOrderItem={setOrderItem}
            groupCount={groupCount}
          />
        ))}
      </div>

      <div className="mt-4 flex justify-between">
        <SecondaryButton
          disabled={!orderItem.foodItem}
          text="Previous (Select food item)"
          onClick={() => setStep(1)}
        />

        <PrimaryButton
          disabled={!orderItem.foodItem}
          text="Next (Choose Customizations)"
          onClick={() => setStep(3)}
        />
      </div>
    </>
  );
};

const FoodGroupSection = ({
  group,
  orderItem,
  customizations,
  setOrderItem,
  groupCount,
}: FoodGroupSectionProps) => {
  const section = customizations ? 'customisations' : 'options';

  const getTotalQuantityInGroup = () => {
    return orderItem[section]
      .filter((o) => o.group === group.id)
      .reduce((sum, option) => sum + option.quantity, 0);
  };

  const selectOption = (option: Option, group: FoodGroup) => {
    const tempOrderItem = { ...orderItem };
    let optionSelected = tempOrderItem[section].find(
      (o) => o.option === option.id
    );

    if (group.maximumSelection === 1 && group.minimumSelection === 1) {
      if (optionSelected) {
        return;
      }

      // Remove any previously selected option from this group
      tempOrderItem[section] = tempOrderItem[section].filter(
        (o) => o.group !== group.id
      );

      // Add the new selection
      tempOrderItem[section].push({
        option: option.id!,
        quantity: 1,
        price: getOptionPrice(option),
        name: option.name ?? '',
        group: group.id,
      });
    } else {
      // Checkbox behavior for other cases
      if (optionSelected) {
        tempOrderItem[section] = tempOrderItem[section].filter(
          (o) => o.option !== option.id
        );
      } else {
        tempOrderItem[section].push({
          option: option.id!,
          quantity: 1,
          price: getOptionPrice(option),
          name: option.name ?? '',
          group: group.id,
        });
      }
    }

    setOrderItem(tempOrderItem);
  };
  const updateQuantity = (option: Option, change: number) => {
    const tempOrderItem = { ...orderItem };
    let optionSelected = tempOrderItem[section].find(
      (o) => o.option === option.id
    );

    if (!optionSelected) {
      // If option not selected and we're increasing, add it
      if (change > 0) {
        const currentTotal = getTotalQuantityInGroup();
        const newTotal = currentTotal + change;

        // Check if we exceed maximum (only if maximum is defined)
        if (group.maximumSelection && newTotal > group.maximumSelection) {
          return; // Don't allow exceeding maximum
        }

        tempOrderItem[section].push({
          option: option.id!,
          quantity: change,
          price: getOptionPrice(option),
          name: option.name ?? '',
          group: group.id,
        });
      }
    } else {
      const newQuantity = optionSelected.quantity + change;

      if (newQuantity <= 0) {
        tempOrderItem[section] = tempOrderItem[section].filter(
          (o) => o.option !== option.id
        );
      } else {
        // Check if new total would exceed maximum (only if maximum is defined)
        if (group.maximumSelection) {
          const currentTotal = getTotalQuantityInGroup();
          const newTotal = currentTotal + change;

          if (newTotal > group.maximumSelection) {
            return; // Don't allow exceeding maximum
          }
        }
        // Update quantity
        optionSelected.quantity = newQuantity;
      }
    }
    setOrderItem(tempOrderItem);
  };

  const getOptionQuantity = (option: Option) => {
    const selected = orderItem[section].find((o) => o.option === option.id);
    return selected ? selected.quantity : 0;
  };

  React.useEffect(() => {
    const tempOrderItem = { ...orderItem };

    if (
      group.minimumSelection === 1 &&
      !tempOrderItem[section].find((i) => i.group === group.id) &&
      group.options &&
      group.options.length > 0
    ) {
      tempOrderItem[section].push({
        option: group.options[0]?.id ?? '',
        quantity: 1,
        price: getOptionPrice(group.options[0]),
        name: group.options[0].name ?? '',
        group: group.id,
      });

      setOrderItem(tempOrderItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRadioButton =
    group.maximumSelection === 1 && group.minimumSelection === 1;
  const hasQuantityControls = !isRadioButton;
  const hasMaximumLimit = group.maximumSelection && group.maximumSelection >= 1;

  return (
    <div
      className={`mt-2 overflow-y-scroll border-b-2 border-neutral-300 ${
        (groupCount ?? 0) > 2 ? 'h-[200px]' : 'h-[400px]'
      }`}
    >
      <div className="flex justify-between items-center">
        <p className="text-neutral-700 text-left">{group.name}</p>
        <p className="text-xs text-neutral-500 semibold">
          {group.minimumSelection === 0
            ? 'Optional'
            : hasMaximumLimit
            ? `${group.maximumSelection} required`
            : null}
        </p>
      </div>
      <div>
        {group.options?.map((option) => {
          const quantity = getOptionQuantity(option);
          const isSelected =
            orderItem[section]?.some((o) => o.option === option.id) || false;
          return (
            <div
              key={option.id}
              className={`flex space-x-6 mt-4 border border-neutral-200 text-neutral-800 rounded-lg bg-[#FCF7F8]`}
            >
              <div
                className={`flex-1 px-3 py-2 ${
                  isRadioButton ? 'cursor-pointer' : ''
                }`}
                onClick={() =>
                  isRadioButton ? selectOption(option, group) : null
                }
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                    <img
                      src={
                        option.image
                          ? option.image
                          : option.sameAsFoodItem?.imageUrl
                          ? option.sameAsFoodItem?.imageUrl
                          : ''
                      }
                      alt="option"
                    />
                  </div>
                  <div className="flex flex-col items-start flex-1">
                    <p className="text-sm">{option.name}</p>
                    <p className="text-xs">{`${naira} ${numeral(
                      option.price
                    ).format('0,0')}`}</p>
                  </div>
                  {isRadioButton && (
                    <SvgRadio
                      className={`${
                        isSelected ? 'text-primary-600' : 'text-white'
                      }`}
                    />
                  )}
                </div>
              </div>
              {hasQuantityControls && (
                <div className="flex items-center gap-2 flex-shrink-0 pr-3">
                  {quantity >= 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(option, -1);
                        }}
                      >
                        <img src={minusIcon} className="h-3 w-3" alt="minus" />
                      </button>
                      <span className="text-base medium">{quantity}</span>
                    </>
                  )}
                  {!(
                    (group.maximumSelection ?? 0) > 1 &&
                    getTotalQuantityInGroup() >=
                      (group.maximumSelection ?? 0) &&
                    quantity < 1
                  ) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateQuantity(option, 1);
                      }}
                    >
                      {(group.maximumSelection ?? 0) >= 1 &&
                      getTotalQuantityInGroup() >=
                        (group.maximumSelection ?? 0) ? (
                        <SvgCheck className=" text-red-600" />
                      ) : (
                        //  <img src={`${SvgCheck}`} className="h-3" alt="add"  color="#C72C3F"/>
                        <img src={add} className="h-3" alt="add" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SelectCustomizations = ({
  foodItems,
  orderItem,
  addItem,
  setStep,
  setOrderItem,
}: SelectOptionsProps) => {
  const item = foodItems.find((i) => i.id === orderItem.foodItem);

  return (
    <>
      <p className="text-xs text-left mt-2">
        Select customizations for this food item
      </p>

      <div className="grid grid-cols-2 gap-4 mt-7 h-[400px] overflow-y-auto items-start">
        {item &&
          (item.customisations as FoodGroup[])?.map((group) => (
            <FoodGroupSection
              key={group.id}
              group={group}
              orderItem={orderItem}
              setOrderItem={setOrderItem}
              customizations
            />
          ))}
      </div>

      <div className="mt-4 flex justify-between">
        <SecondaryButton
          text="Previous (Select options)"
          onClick={() => setStep(2)}
        />

        <PrimaryButton
          disabled={!orderItem.foodItem}
          text="Add Item"
          onClick={() => addItem?.()}
        />
      </div>
    </>
  );
};

export const FoodItemQuantity = ({
  orderItem,
  setOrderItem,
}: FoodItemQuantityProps) => {
  return (
    <>
      <div className="items-center flex justify-center space-x-3">
        <button
          disabled={orderItem.quantity === 1}
          onClick={() =>
            setOrderItem({ ...orderItem, quantity: orderItem.quantity - 1 })
          }
        >
          <img src={minusIcon} className="h-3 w-3" alt="add" />
        </button>

        <p className="text-lg bold">{orderItem.quantity}</p>
        <button
          onClick={() =>
            setOrderItem({ ...orderItem, quantity: orderItem.quantity + 1 })
          }
        >
          <img src={add} className="h-3" alt="add" />
        </button>
      </div>
    </>
  );
};
